import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../components/Auth/AuthContext';
import LoadingScreen from '../components/common/LoadingScreen';

const { FiCheck, FiAlertCircle } = FiIcons;

const PaymentSuccessPage = () => {
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const navigate = useNavigate();
  const location = useLocation();
  const { updatePremiumStatus, loading } = useAuth();

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get session ID from URL
        const urlParams = new URLSearchParams(location.search);
        const sessionId = urlParams.get('session_id');
        
        if (!sessionId) {
          setVerificationStatus('error');
          return;
        }
        
        // Update premium status in database
        const { error } = await updatePremiumStatus(true, sessionId);
        
        if (error) {
          console.error('Error updating premium status:', error);
          setVerificationStatus('error');
          return;
        }
        
        setVerificationStatus('success');
        
        // Redirect after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } catch (error) {
        console.error('Payment verification error:', error);
        setVerificationStatus('error');
      }
    };

    // Only verify once auth is loaded
    if (!loading) {
      verifyPayment();
    }
  }, [location, navigate, updatePremiumStatus, loading]);

  if (loading) {
    return <LoadingScreen message="Preparing your account..." />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
      >
        {verificationStatus === 'verifying' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-600 border-t-transparent mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Verifying Your Payment</h2>
            <p className="text-gray-600">
              Please wait while we confirm your payment with Stripe...
            </p>
          </>
        )}
        
        {verificationStatus === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiCheck} className="text-3xl text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for upgrading to Pro! You now have unlimited access to the meal calculator.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800 font-medium">
                Redirecting you back to the calculator...
              </p>
            </div>
          </>
        )}
        
        {verificationStatus === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiAlertCircle} className="text-3xl text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Verification Failed</h2>
            <p className="text-gray-600 mb-6">
              We couldn't verify your payment. This could be due to a technical issue or the payment was cancelled.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
            >
              Return to Calculator
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;